import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useVenueAuth } from "../contexts/VenueAuthContext";
import { useVenue } from "../contexts/VenueContext";
import VenueSwitcher from "./VenueSwitcher";
import NotificationBell from "./NotificationBell";
import NotificationCenter from "./NotificationCenter";
import { useNotifications } from "../hooks/useNotifications";

type HeaderProps = {
  onToggleSidebar: () => void;
  isSidebarCollapsed: boolean;
  isMobileViewport: boolean;
};

export default function Header({ onToggleSidebar }: HeaderProps) {
  const navigate = useNavigate();
  const { profile, signOut } = useVenueAuth();
  const { currentVenue } = useVenue();
  const isMallOperator = profile?.role === "property_operator";
  const displayName = profile?.display_name_raw || profile?.display_name;
  const headerTitle = isMallOperator
    ? (displayName || "Mall Operator")
    : (currentVenue?.name || "VenueHub");
  const [unreadCount] = useState(0); // TODO: Fetch from interactions service
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] =
    useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationCenterRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount: notificationUnreadCount,
    loading: notificationsLoading,
    error: notificationsError,
    refetch: refetchNotifications,
    markAsRead,
    deleteNotifications,
    clearAllNotifications,
  } = useNotifications();

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }

      if (
        notificationCenterRef.current &&
        !notificationCenterRef.current.contains(event.target as Node)
      ) {
        setIsNotificationCenterOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
        setIsNotificationCenterOpen(false);
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleSettingsClick = () => {
    navigate("/settings");
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <header className="fixed left-0 right-0 top-0 z-50 h-16 border-b border-gray-200 bg-white px-3 py-4 md:px-6">
      <div className="flex h-full items-center justify-between">
        <div className="flex items-center space-x-1 md:space-x-4">
          <button
            onClick={onToggleSidebar}
            className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-50"
            aria-label="Toggle sidebar"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <div className="items-center space-x-2 hidden md:flex">
            {currentVenue?.logo_url && !isMallOperator ? (
              <img
                src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/venues/${currentVenue.logo_url}`}
                alt={headerTitle}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-emerald-600">
                <span className="text-sm font-bold text-white">
                  {headerTitle.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <h1 className="text-xl font-bold text-gray-900">
              {headerTitle}
            </h1>
          </div>
          {profile?.role !== "property_operator" && (
            <div className=" items-center space-x-3 md:flex">
              <div className="ml-0 md:ml-2">
                <VenueSwitcher />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative" ref={notificationCenterRef}>
            <NotificationBell
              unreadCount={notificationUnreadCount}
              isOpen={isNotificationCenterOpen}
              onClick={() => {
                setIsDropdownOpen(false);
                setIsNotificationCenterOpen((isOpen) => !isOpen);
              }}
            />

            <NotificationCenter
              isOpen={isNotificationCenterOpen}
              onClose={() => setIsNotificationCenterOpen(false)}
              notifications={notifications}
              loading={notificationsLoading}
              error={notificationsError}
              onMarkAsRead={markAsRead}
              onDeleteNotifications={deleteNotifications}
              onClearAll={clearAllNotifications}
              onRefetch={refetchNotifications}
            />
          </div>
          <button
            onClick={handleSettingsClick}
            className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-50"
            aria-label="Settings"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center space-x-2 rounded-full p-1 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="User menu"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200">
                <span className="text-sm font-medium text-gray-700">
                  {displayName?.charAt(0).toUpperCase() || "U"}
                </span>
              </div>
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                <div className="border-b border-gray-100 px-4 py-3">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {displayName || "User"}
                  </p>
                  <p className="text-xs capitalize text-gray-500">
                    {profile?.role === "venue_owner"
                      ? "Venue Owner"
                      : profile?.role === "property_operator"
                        ? "Mall Operator"
                        : profile?.staffRole || "Staff"}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsDropdownOpen(false);
                    navigate("/settings");
                  }}
                  className="flex w-full items-center px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <svg
                    className="mr-3 h-4 w-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Settings
                </button>
                <button
                  onClick={() => {
                    setIsDropdownOpen(false);
                    handleSignOut();
                  }}
                  className="flex w-full items-center px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <svg
                    className="mr-3 h-4 w-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
